import styled from '@emotion/styled';
import { themeGet } from '@styled-system/theme-get';
import menuOpenedSvg from '../../../assets/images/solutions/menu-opened.svg';
import menuClosedSvg from '../../../assets/images/solutions/menu-closed.svg';

export const StyledContainer = styled.nav`
  background: ${themeGet('color.colorWhite')};
  box-shadow: 0 0 1px 0 #a8b9d5, 0 7px 11px -4px rgba(0, 23, 62, 0.2);
  text-align: left;
  position: relative;
  z-index: ${themeGet('zIndex.zIndex02')};

  ul,
  li {
    list-style: none;
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    display: grid;
    grid-template-columns: max-content max-content;
    justify-content: space-between;

    > div,
    > div > div {
      height: 100%;
    }
  }
`;

export const StyledMenu = styled.div`
  overflow: hidden;

  > div > ul::before {
    content: '';
    display: block;
    height: ${themeGet('scale.scaleDown05')};
    background: ${themeGet('color.colorGray300')};
    margin: 0 ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleBase')};
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    overflow: visible;

    > div > ul {
      display: flex;
      height: 100%;

      > li {
        position: relative;
      }

      &::before {
        display: none;
      }
    }
  }
`;

export const StyledMenuItem = styled.div`
  padding: ${themeGet('scale.scaleDown02')} ${themeGet('scale.scaleUp03')};
  border-style: solid;
  border-width: 0 0 0 ${themeGet('scale.scaleDown04')};
  border-color: ${(props) =>
    props.isActive ? themeGet('color.colorBlue') : 'transparent'};

  a,
  button {
    ${themeGet('font.whitneyMedium')}
    background: none;
    border: none;
    text-decoration: none;
    padding: 0;
    color: ${themeGet('color.colorGrayBlue70')};
    font-size: ${themeGet('scale.scaleBase')};
    cursor: pointer;
  }

  button::after {
    content: '';
    display: inline-block;
    width: 16px;
    height: 9px;
    margin-left: ${themeGet('scale.scaleDown03')};
    background-size: contain;
    background-position: center center;
    background-image: ${(props) =>
      props.isOpen ? `url(${menuOpenedSvg}) ` : `url(${menuClosedSvg}) `};
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    border-width: 0 0 ${themeGet('scale.scaleDown04')};
    padding: 0;
    padding-top: ${themeGet('scale.scaleDown04')};
    height: 100%;
    display: flex;
    align-items: center;
    margin-right: ${themeGet('scale.scaleUp05')};

    a,
    button {
      font-size: ${themeGet('scale.scaleDown01')};
    }

    button::after {
      width: 11px;
      height: 6px;
      margin-left: ${themeGet('scale.scaleDown04')};
    }
  }
`;

export const StyledSubMenu = styled.div`
  background: ${themeGet('color.colorWhite')};
  overflow: hidden;

  ul {
    border: ${themeGet('scale.scaleUp03')} solid ${themeGet('color.colorWhite')};
    border-top-width: 0;
    border-bottom-width: ${(props) =>
      props.isLast ? themeGet('scale.scaleUp03') : 0};
    background: ${themeGet('color.colorBlue10')};
    padding: ${themeGet('scale.scaleDown02')} 0;
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    position: absolute;
    bottom: 0;
    right: 0;
    white-space: nowrap;
    z-index: ${themeGet('zIndex.zIndex01')};
    transform: translate(0, 100%);
    display: flex;
    align-items: flex-end;
    box-shadow: 0 0 1px 0 #a8b9d5, 0 7px 11px -4px rgba(0, 23, 62, 0.2);

    ul {
      border: ${themeGet('scale.scaleBase')} solid
        ${themeGet('color.colorWhite')};
      padding: ${themeGet('scale.scaleDown03')} 0;
    }
  }
`;

export const StyledSubMenuItem = styled.li`
  ${(props) =>
    !props.isAdditional && props.isActive && themeGet('font.whitneySemibold')}
  ${(props) => props.isAdditional && themeGet('font.whitneyMedium')}
  padding: ${themeGet('scale.scaleDown02')} ${themeGet('scale.scaleUp03')};
  border-style: solid;
  border-left-width: 3px;
  border-color: ${(props) =>
    props.isActive ? themeGet('color.colorBlue') : 'transparent'};

  a {
    text-decoration: none;
    color: ${(props) =>
      props.isAdditional
        ? themeGet('color.colorBlue')
        : themeGet('color.colorGray900')};
    
    &::after {
      content: ${(props) => (props.isAdditional ? "' ⇢'" : undefined)};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    padding: ${themeGet('scale.scaleDown03')} ${themeGet('scale.scaleBase')};
    font-size: ${themeGet('scale.scaleDown01')};
  }
`;

export const StyledTitle = styled.figure`
  display: grid;
  padding: ${themeGet('scale.scaleDown02')} ${themeGet('scale.scaleUp03')};
  grid-template-columns: 32px auto;
  grid-column-gap: ${themeGet('scale.scaleDown01')};
  align-items: center;

  figcaption {
    ${themeGet('font.whitneyMedium')}
    color: ${themeGet('color.colorGrayBlue70')};

    &[role="button"] {
      position: relative;
      cursor: pointer;

      &::after {
        content: '';
        display: block;
        width: 16px;
        height: 9px;
        position: absolute;
        right: 0;
        top: 50%;
        margin-top: -4.5px;
        background: ${(props) =>
          props.isMenuOpen
            ? `url(${menuOpenedSvg}) `
            : `url(${menuClosedSvg}) `}
      }
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    font-size: ${themeGet('scale.scaleDown01')};

    figcaption[role="button"] {
      cursor: initial;
      &::after {
        display: none;
      }
    }
  }
`;
